import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import BackButton from "../base/BackButton";
import CustomInput from "../base/CustomInput";
import * as FaIcons from "react-icons/fa";
import Button from "../base/Button";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";

function AttachCheckListToProviders() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.assignment || null;

  const [permissions, setPermissions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [providers, setProviders] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);

  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);

  const [checklistSearch, setChecklistSearch] = useState("");
  const [filteredChecklists, setFilteredChecklists] = useState([]);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState([]);

  const [selectedApplicants, setSelectedApplicants] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (editData) {
      setIsEdit(true);
      setSelectedCategory(editData.provider?.categoryId || "");
      setSelectedProviderId(editData.provider?.id || null);
      setSelectedCourseIds(editData.course ? [editData.course.id] : []);
      setSelectedCategoryIds(
        editData.checklistCategory ? [editData.checklistCategory.id] : []
      );
      setSelectedChecklistIds(
        editData.checklist ? [editData.checklist.id] : []
      );
      setSelectedApplicants(
        editData?.applicant_type ? [editData.applicant_type.id] : []
      );
    }
  }, [editData]);

  const handleSave = async () => {
    const provider = providers.find((p) => p.id === selectedProviderId);
    const providerData = provider
      ? { id: provider.id, name: provider.name }
      : null;

    const selectedCourses = courses
      .filter((c) => selectedCourseIds.includes(c.id))
      .map((c) => ({ id: c.id, name: c.name }));

    const selectedChecklistCategories =
      categories?.checklist_category
        ?.filter((cat) => selectedCategoryIds.includes(cat.id))
        .map((cat) => ({ id: cat.id, name: cat.name })) || [];

    const selectedChecklists =
      categories?.checklist
        ?.filter((cl) => selectedChecklistIds.includes(cl.id))
        .map((cl) => {
          const category = categories?.checklist_category?.find(
            (c) => c.id === cl.categoryid
          );
          return {
            id: cl.id,
            title: cl.title,
            categoryId: cl.categoryid,
            categoryName: category?.name || "—",
          };
        }) || [];

    const payload = {
      provider: providerData,
      courses: selectedCourses,
      checklistCategories: selectedChecklistCategories,
      checklists: selectedChecklists,
      applicants: selectedApplicants.map((id) => ({ id })),
    };

    try {
      setLoading(true);
      const response = await apiUrl.post(
        "/provider-checklist-assignment",
        payload
      );

      if (response?.data?.status) {
        Swal.fire({
          title: response?.data?.message || "Assignments saved successfully.",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        navigate("/checklist/attach");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Something went wrong";

      Swal.fire({
        title: message,
        icon: "error",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Array.isArray(categories?.checklist)) {
      setFilteredChecklists([]);
      return;
    }

    let result = categories.checklist;

    if (selectedCategoryIds.length > 0) {
      result = result.filter((item) =>
        selectedCategoryIds.includes(item.categoryid)
      );
    }

    if (checklistSearch) {
      result = result.filter((item) =>
        item.title.toLowerCase().includes(checklistSearch.toLowerCase())
      );
    }

    setFilteredChecklists(result);
  }, [checklistSearch, selectedCategoryIds, categories?.checklist]);

  useEffect(() => {
    if (!categories?.checklist) return;

    let result = categories.checklist;

    // Filter by selected categories
    if (selectedCategoryIds.length > 0) {
      result = result.filter((item) =>
        selectedCategoryIds.includes(item.categoryid)
      );
    }

    // Apply search
    if (checklistSearch) {
      result = result.filter((item) =>
        item.title.toLowerCase().includes(checklistSearch.toLowerCase())
      );
    }

    setFilteredChecklists(result);
  }, [checklistSearch, selectedCategoryIds, categories?.checklist]);

  useEffect(() => {
    if (!categories?.checklist_category) {
      setFilteredCategories([]);
      return;
    }

    const filtered = categories.checklist_category.filter((cat) =>
      cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
    setFilteredCategories(filtered);
  }, [categorySearch, categories?.checklist_category]);

  useEffect(() => {
    if (!categories?.checklist) return;

    let result = categories.checklist;

    if (selectedCategoryIds.length > 0) {
      result = result.filter((item) =>
        selectedCategoryIds.includes(item.categoryid)
      );
    }

    if (checklistSearch) {
      result = result.filter((item) =>
        item.title.toLowerCase().includes(checklistSearch.toLowerCase())
      );
    }

    setFilteredChecklists(result);
  }, [checklistSearch, selectedCategoryIds, categories?.checklist]);

  const handleChecklistToggle = (checklistId) => {
    setSelectedChecklistIds((prev) =>
      prev.includes(checklistId)
        ? prev.filter((id) => id !== checklistId)
        : [...prev, checklistId]
    );
  };

  useEffect(() => {
    const pers = getPermissions();
    if (pers) setPermissions(pers);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const getProvidersByNames = async () => {
    if (!selectedCategory) {
      setProviders([]);
      return;
    }
    try {
      const response = await apiUrl(`/getProvidersByNames/${selectedCategory}`);
      if (response?.data?.status) {
        setProviders(
          Array.isArray(response?.data?.data) ? response?.data?.data : []
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getProvidersByNames();
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedProviderId(null);
    setSelectedCourseIds([]);
    setProviders([]);
    setCourses([]);
    setProviderSearch("");
    setCourseSearch("");
  }, [selectedCategory]);

  const fetchCourses = async () => {
    if (!selectedProviderId) {
      setCourses([]);
      setSelectedCourseIds([]);
      return;
    }
    try {
      const response = await apiUrl.get(
        `/getCourseByNames/${selectedProviderId}`
      );
      if (response?.data?.status) {
        setCourses(
          Array.isArray(response?.data?.data) ? response?.data?.data : []
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error(error);
      setCourses([]);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [selectedProviderId]);

  useEffect(() => {
    setSelectedCourseIds([]);
    setCourseSearch("");
  }, [selectedProviderId]);

  const fetchCategories = async () => {
    try {
      const response = await apiUrl.get("/prechecklistCategory");
      if (response?.data?.status) {
        setCategories(response?.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!providerSearch) {
      setFilteredProviders(providers);
    } else {
      const filtered = providers.filter((provider) =>
        provider.name.toLowerCase().includes(providerSearch.toLowerCase())
      );
      setFilteredProviders(filtered);
    }
  }, [providerSearch, providers]);

  useEffect(() => {
    if (!courseSearch) {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter((course) =>
        course.name.toLowerCase().includes(courseSearch.toLowerCase())
      );
      setFilteredCourses(filtered);
    }
  }, [courseSearch, courses]);

  const handleCourseToggle = (courseId) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleApplicantToggle = (applicantId) => {
    setSelectedApplicants((prev) =>
      prev.includes(applicantId)
        ? prev.filter((id) => id !== applicantId)
        : [...prev, applicantId]
    );
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <>
      {permissions?.manage_checklist && (
        <div className="card px-4 py-2 bg-white mb-10 relative">
          <header className="mb-6 flex items-center gap-3">
            <BackButton url="/checklist/attach" />
            <h1 className="text-2xl font-bold text-slate-900">
              Attach Checklist to Provider
            </h1>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Panel - Provider Type */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  Provider Type
                </h2>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-slate-300 rounded p-2 text-slate-900 focus:ring focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">-- Select Category --</option>
                  {categories?.category?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Middle Panel - Providers (Single Select) */}
            <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-between">
                Providers
              </h2>
              <div className="mb-4 relative">
                <CustomInput
                  placeholder="Search ..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "18rem" }}>
                {filteredProviders?.map((provider) => (
                  <label
                    key={provider?.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-left"
                  >
                    <input
                      type="radio"
                      name="provider"
                      checked={selectedProviderId === provider?.id}
                      onChange={() => setSelectedProviderId(provider?.id)}
                    />
                    <span className="text-sm font-medium text-foreground truncate">
                      {provider?.name}
                    </span>
                  </label>
                ))}
                {filteredProviders.length === 0 && (
                  <p className="text-sm text-gray-500 p-3 text-center">
                    {selectedCategory
                      ? "No providers found"
                      : "Select a Provider"}
                  </p>
                )}
              </div>
            </div>
            {/* Right Panel - Courses / Visa (Multiple Select) */}
            <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
              <div className="flex justify-between">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-between gap-2">
                  Course / Visa
                  {selectedCourseIds.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {selectedCourseIds.length} selected
                    </span>
                  )}
                </h2>
                <div>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() =>
                      setSelectedCourseIds(courses?.map((c) => c.id))
                    }
                    disabled={courses.length === 0 || !selectedProviderId}
                    text="Select All"
                  />
                </div>
              </div>
              <div className="mb-4 relative">
                <CustomInput
                  placeholder="Search ..."
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  disabled={!selectedProviderId}
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "18rem" }}>
                {filteredCourses?.map((course) => (
                  <label
                    key={course?.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-left"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourseIds.includes(course?.id)}
                      onChange={() => handleCourseToggle(course?.id)}
                    />
                    <span className="text-sm font-medium text-foreground truncate">
                      {course?.name}
                    </span>
                  </label>
                ))}
                {selectedProviderId && filteredCourses.length === 0 && (
                  <p className="text-sm text-gray-500 p-3 text-center">
                    No courses found
                  </p>
                )}
                {!selectedProviderId && (
                  <p className="text-sm text-gray-500 p-3 text-center">
                    Select a Course or Visa
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
            {/* checklist category */}
            <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-between">
                Category
                {selectedCategoryIds?.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {selectedCategoryIds?.length} selected
                  </span>
                )}
              </h2>
              <div className="mb-4 relative">
                <CustomInput
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "18rem" }}>
                {filteredCategories.map((category) => (
                  <label
                    key={category?.id}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-left"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds?.includes(category?.id)}
                      onChange={() => handleCategoryToggle(category?.id)}
                    />
                    <span className="text-sm font-medium text-foreground truncate">
                      {category?.name}
                    </span>
                  </label>
                ))}
                {filteredCategories.length === 0 && (
                  <p className="text-sm text-gray-500 p-3 text-center">
                    {categorySearch
                      ? "No categories found"
                      : "No categories available"}
                  </p>
                )}
              </div>
            </div>
            {/* checklists */}
            <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-between">
                Checklists
                {selectedChecklistIds?.length > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    {selectedChecklistIds?.length} selected
                  </span>
                )}
              </h2>

              <div className="mb-4 relative">
                <CustomInput
                  placeholder="Search checklists..."
                  value={checklistSearch}
                  onChange={(e) => setChecklistSearch(e.target.value)}
                  disabled={selectedCategoryIds?.length === 0}
                />
              </div>

              <div className="overflow-y-auto" style={{ maxHeight: "18rem" }}>
                {filteredChecklists.length > 0 ? (
                  filteredChecklists?.map((checklist) => {
                    const category = categories?.checklist_category?.find(
                      (c) => c.id === checklist?.categoryid
                    );

                    return (
                      <label
                        key={checklist?.id}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-left"
                      >
                        <input
                          type="checkbox"
                          checked={selectedChecklistIds.includes(checklist?.id)}
                          onChange={() => handleChecklistToggle(checklist?.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground truncate block">
                            {checklist?.title}
                          </span>
                          {category && (
                            <span className="text-xs text-gray-500">
                              {category?.name}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500 p-3 text-center">
                    {selectedCategoryIds.length === 0
                      ? "Select at least one category"
                      : "No checklists found"}
                  </p>
                )}
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-lg border border-border p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center justify-between">
                  Applicant
                  {selectedApplicants.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {selectedApplicants.length} selected
                    </span>
                  )}
                </h2>
                <div className="space-y-2">
                  {[
                    { id: 1, label: "Main" },
                    { id: 2, label: "Spouse" },
                    { id: 3, label: "Child" },
                    { id: 4, label: "Sponsor" },
                  ].map((applicant) => (
                    <label
                      key={applicant.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedApplicants.includes(applicant.id)}
                        onChange={() => handleApplicantToggle(applicant.id)}
                        className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                      />
                      <span className="text-sm font-medium text-foreground">
                        {applicant.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mb-4 mt-2 flex justify-end">
            <Button
              onClick={handleSave}
              isDisabled={
                !selectedProviderId ||
                selectedCourseIds.length === 0 ||
                selectedChecklistIds.length === 0 ||
                selectedApplicants.length === 0
              }
              isLoading={loading}
              variant="primary"
              loadingText="Saving..."
              size="lg"
              text="Save"
              style={{ width: "full" }}
            ></Button>
          </div>
        </div>
      )}
    </>
  );
}

export default AttachCheckListToProviders;
